import { useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Link, TableSortLabel, Typography } from "@mui/material";
import { parse } from "date-fns";
import ScheduleView from "features/schedule-view/ScheduleView";
import { TimeEntry } from "models/TimeEntry";
import { sv } from "date-fns/locale";
import { absenceTypeToString } from "utilities/formatAbsence";
import { absenceTypeToColor } from "utilities/formatAbsence";
import { absenceStateToString } from "utilities/formatAbsence";
import { formatDateTime } from "utilities/formatDateTime";
import { useAuthContext } from "features/auth/AuthContext";
import { useTimeEntriesContext } from "../TimeEntriesContext";
import { Creator } from "models/Checklist";

type TimeEntryScheduleProps = {
  rows: Creator[];
  filters: {
    fromDate: Date;
    toDate: Date;
  };
  view: "WEEKS" | "MONTHS" | "WORKWEEKS";
  showDayNumbers: boolean;
  sortBy: string;
  sortDir: "asc" | "desc";
  setSortDir: (dir: "asc" | "desc") => void;
  setSortBy: (key: keyof TimeEntry) => void;
};

const TimeEntrySchedule: React.FC<TimeEntryScheduleProps> = (props) => {
  const { rows, filters, view, showDayNumbers, sortBy, sortDir, setSortDir, setSortBy } = props;
  const { entries, selectedEntries, setSelectedEntries } = useTimeEntriesContext();
  const { user } = useAuthContext();
  const isAdminOrPayroll = user?.role === "ADMIN" || user?.role === "PAYROLL";

  const scheduleData = useMemo(
    () =>
      entries.map((entry) => ({
        ...entry,
        from: parse(entry.from, "yyyy-MM-dd HH:mm", new Date(), { locale: sv }),
        to: parse(entry.to, "yyyy-MM-dd HH:mm", new Date(), { locale: sv }),
      })),
    [entries],
  );

  return (
    <ScheduleView
      rows={rows}
      entries={scheduleData}
      groupById={(entry) => entry.userId}
      fromDate={filters.fromDate}
      toDate={filters.toDate}
      scale={view}
      enableSelection={isAdminOrPayroll}
      selectedEntries={selectedEntries}
      setSelectedEntries={(entryIds) => setSelectedEntries(entryIds as string[])}
      showDayNumbers={showDayNumbers}
      firstColumnHeader={
        <TableSortLabel
          active={sortBy === "userName"}
          direction={sortBy === "userName" ? sortDir : "asc"}
          onClick={() =>
            sortBy === "userName"
              ? setSortDir(sortDir === "asc" ? "desc" : "asc")
              : setSortBy("userName")
          }
        >
          Medarbetare
        </TableSortLabel>
      }
      renderRowHeader={([row]) => (
        <Link component={RouterLink} to={`/users/view/${row.id}`}>
          {row.userName}
        </Link>
      )}
      entryProps={(entry) => ({
        color: absenceTypeToColor(entry.type),
        state:
          entry.state === "PENDING"
            ? "tentative"
            : entry.state === "REJECTED"
              ? "cancelled"
              : "confirmed",
        tooltip: (
          <Box textAlign="center">
            {isAdminOrPayroll && (
              <>
                <Typography fontSize="inherit" fontWeight="bold">
                  {absenceTypeToString(entry.type)}
                </Typography>
                <Typography fontSize="inherit">{absenceStateToString(entry.state)}</Typography>
              </>
            )}
            <Typography fontSize="inherit">Från: {formatDateTime(entry.from)}</Typography>
            <Typography fontSize="inherit">Till: {formatDateTime(entry.to)}</Typography>
          </Box>
        ),
      })}
    />
  );
};

export default TimeEntrySchedule;
